/**
 * Iteration 43c — UTM attribution.
 *
 * Captures utm_* query params from the current URL into localStorage the
 * first time a landing lands (7-day TTL, last-touch wins). The application
 * start flow reads these back and posts them to the API — see
 * PublicJobDetailPage / CandidateAuthPages / apply flow.
 *
 * Usage:
 *   import { captureUtmFromUrl, readUtm, clearUtm } from "@/lib/utm";
 *   useEffect(() => { captureUtmFromUrl(); }, []);
 */
const KEY = "bmi_utm_v1";
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

const FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

export function captureUtmFromUrl() {
    if (typeof window === "undefined") return null;
    try {
        const url = new URL(window.location.href);
        const captured = {};
        for (const f of FIELDS) {
            const v = url.searchParams.get(f);
            if (v && v.length <= 160) captured[f] = v.slice(0, 160);
        }
        if (Object.keys(captured).length === 0) return null;
        // Merge with any prior record (last-touch wins per-field, so a
        // landing with only utm_source doesn't wipe an earlier utm_campaign).
        const prior = readUtm() || {};
        const record = { ...prior, ...captured, captured_at: Date.now() };
        window.localStorage.setItem(KEY, JSON.stringify(record));
        return record;
    } catch {
        return null;
    }
}

export function readUtm() {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(KEY);
        if (!raw) return null;
        const rec = JSON.parse(raw);
        if (!rec?.captured_at || Date.now() - rec.captured_at > TTL_MS) {
            window.localStorage.removeItem(KEY);
            return null;
        }
        const out = {};
        for (const f of FIELDS) if (rec[f]) out[f] = rec[f];
        return Object.keys(out).length ? out : null;
    } catch {
        return null;
    }
}

export function clearUtm() {
    if (typeof window === "undefined") return;
    try { window.localStorage.removeItem(KEY); } catch { /* noop */ }
}
